// GLP-1 dose->units calculator (semaglutide / tirzepatide, compounded vials).
// Pure math is exported for unit testing; DOM wiring is guarded so the module
// imports cleanly under node --test. No drug names drive the maths - it is pure
// arithmetic on the numbers the user enters. Not medical advice.

// --- Pure math (unit-tested in tests/glp1-calc-math.test.mjs) ------------------

export function concentrationMgPerMl(vialMg, totalMl) {
  return vialMg / totalMl;
}

export function volumeMl(doseMg, concMgPerMl) {
  return doseMg / concMgPerMl;
}

// U-100 insulin syringe: 1 mL = 100 units.
export const UNITS_PER_ML = {
  'U-100': 100,
};

export function unitsForVolume(volMl, syringe) {
  const upm = UNITS_PER_ML[syringe];
  if (!upm) throw new Error(`Unknown syringe type: ${syringe}`);
  return volMl * upm;
}

export function dosesPerVial(vialMg, doseMg) {
  return Math.floor(vialMg / doseMg);
}

export const LIMITS = {
  vial_mg:        { min: 0.1,  max: 200 },
  total_ml:       { min: 0.1,  max: 20 },
  conc_mg_per_ml: { min: 0.01, max: 200 },
  dose_mg:        { min: 0.001, max: 100 },
};

export function validateField(name, value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return { valid: false, error: 'Enter a number' };
  }
  if (value <= 0) {
    return { valid: false, error: 'Must be greater than 0' };
  }
  const limit = LIMITS[name];
  if (!limit) return { valid: true };
  if (value < limit.min || value > limit.max) {
    return { valid: false, error: `Must be between ${limit.min} and ${limit.max}` };
  }
  return { valid: true };
}

// concMgPerMl may be supplied directly, or derived from vialMg + totalMl.
// Returns { concMgPerMl, volumeMl, units, dosesPerVial } (dosesPerVial null if
// total vial content is unknown).
export function calculate({ vialMg, totalMl, concMgPerMl, doseMg, syringe }) {
  const conc = (concMgPerMl != null) ? concMgPerMl : concentrationMgPerMl(vialMg, totalMl);
  const vol = volumeMl(doseMg, conc);
  const units = unitsForVolume(vol, syringe);
  let totalMg = null;
  if (vialMg != null) totalMg = vialMg;
  else if (concMgPerMl != null && totalMl != null) totalMg = concMgPerMl * totalMl;
  const doses = (totalMg != null) ? dosesPerVial(totalMg, doseMg) : null;
  return { concMgPerMl: conc, volumeMl: vol, units, dosesPerVial: doses };
}

// --- DOM wiring (browser only) -----------------------------------------------
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}

function boot() {
  const root = document.querySelector('.is-glp1');
  if (!root) return;
  const $ = (s) => root.querySelector(s);
  const $$ = (s) => root.querySelectorAll(s);
  const empty = $('#is-glp1-result-empty');
  const ok = $('#is-glp1-result-ok');
  const unitsEl = $('#is-glp1-headline-units');
  const sublineEl = $('#is-glp1-subline');
  const convEl = $('#is-glp1-conversion');

  const state = { vial: null, bac: null, dose: null, syringe: 'U-100' };

  // Result syringe (shared snippet is-calc-syr): draws the pull line on a 1 mL barrel.
  const syrEl = document.getElementById('is-calc-syr');
  const syrRefs = syrEl ? {
    fluid: document.getElementById('is-calc-syr-fluid'),
    seal: document.getElementById('is-calc-syr-seal'),
    plate: document.getElementById('is-calc-syr-plate'),
    rod: document.getElementById('is-calc-syr-rod'),
    ticks: document.getElementById('is-calc-syr-ticks'),
    nums: document.getElementById('is-calc-syr-nums'),
    caption: document.getElementById('is-calc-syr-caption'),
  } : null;
  const SYR = { X: 80, W: 600, TOP: 22, SEAL_W: 14, PLATE_W: 6, ROD_END: 740 };
  function paintSyringe(capacityMl, drawnMl, unitsPerMl) {
    if (!syrRefs || !syrRefs.fluid) return;
    const frac = Math.max(0, Math.min(1, drawnMl / capacityMl));
    const sealX = SYR.X + frac * SYR.W;
    syrRefs.fluid.setAttribute('width', (sealX - SYR.X).toFixed(2));
    syrRefs.seal.setAttribute('x', sealX.toFixed(2));
    syrRefs.plate.setAttribute('x', (sealX + SYR.SEAL_W).toFixed(2));
    const rodStart = sealX + SYR.SEAL_W + SYR.PLATE_W;
    syrRefs.rod.setAttribute('x', rodStart.toFixed(2));
    syrRefs.rod.setAttribute('width', Math.max(0, SYR.ROD_END - rodStart).toFixed(2));
    const totalUnits = capacityMl * unitsPerMl;
    const tickEvery = totalUnits <= 50 ? 1 : 2;
    const labelEvery = totalUnits <= 40 ? 5 : 10;
    const ticks = [];
    const nums = [];
    for (let u = 0; u <= totalUnits + 1e-6; u += tickEvery) {
      const x = SYR.X + (u / totalUnits) * SYR.W;
      const isLabel = Math.abs((u / labelEvery) - Math.round(u / labelEvery)) < 1e-6;
      const h = isLabel ? 7 : 3.5;
      ticks.push('<line class="is-calc__sx-tick" x1="' + x.toFixed(2) + '" y1="' + SYR.TOP + '" x2="' + x.toFixed(2) + '" y2="' + (SYR.TOP + h).toFixed(2) + '" stroke-width="' + (isLabel ? 1.2 : 0.6) + '"/>');
      if (u > 0 && isLabel) nums.push('<text class="is-calc__sx-num" x="' + x.toFixed(2) + '" y="71" font-size="11">' + Math.round(u) + '</text>');
    }
    syrRefs.ticks.innerHTML = ticks.join('');
    syrRefs.nums.innerHTML = nums.join('');
  }
  function unitsPerMl() { return UNITS_PER_ML[state.syringe] || 100; }
  if (syrRefs) {
    paintSyringe(1, 0, unitsPerMl());
    syrRefs.caption.textContent = 'Pick your values to see the pull line to draw.';
  }

  function recompute() {
    if (!isFinite(state.vial) || state.vial <= 0 ||
        !isFinite(state.bac) || state.bac <= 0 ||
        !isFinite(state.dose) || state.dose <= 0) {
      empty.hidden = false; ok.hidden = true;
      paintSyringe(1, 0, unitsPerMl());
      if (syrRefs) syrRefs.caption.textContent = 'Pick your values to see the pull line to draw.';
      writeUrl();
      if (window.announceCalcResult) window.announceCalcResult(null);
      return;
    }
    const r = calculate({ vialMg: state.vial, totalMl: state.bac, doseMg: state.dose, syringe: state.syringe });
    unitsEl.textContent = r.units.toFixed(r.units < 10 ? 1 : 0);
    sublineEl.textContent = '= ' + r.volumeMl.toFixed(3) + ' mL of ' + r.concMgPerMl.toFixed(2) + ' mg/mL solution';
    let conv = state.dose + ' mg at ' + r.concMgPerMl.toFixed(2) + ' mg/mL.';
    if (r.dosesPerVial) conv += ' About ' + r.dosesPerVial + ' doses per vial.';
    convEl.textContent = conv;
    paintSyringe(1, r.volumeMl, unitsPerMl());
    if (syrRefs) syrRefs.caption.textContent = 'Draw to ' + r.units.toFixed(r.units < 10 ? 1 : 0) + ' units on a 1 mL ' + state.syringe + ' insulin syringe';
    empty.hidden = true; ok.hidden = false;
    writeUrl();
    if (window.announceCalcResult) window.announceCalcResult(r.volumeMl);
  }

  // Pill groups with Other fallback (vial / bac / dose)
  ['vial', 'bac', 'dose'].forEach((field) => {
    const pills = $$('.is-calc__pill[data-field="' + field + '"]');
    const otherBlock = root.querySelector('.is-calc__other-input[data-field="' + field + '"]');
    const otherInput = otherBlock ? otherBlock.querySelector('input') : null;
    const errEl = otherBlock ? otherBlock.querySelector('.is-calc__error') : null;
    pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const value = pill.dataset.value;
        pills.forEach((p) => { p.classList.remove('is-calc__pill--active'); p.setAttribute('aria-checked', 'false'); });
        pill.classList.add('is-calc__pill--active');
        pill.setAttribute('aria-checked', 'true');
        if (value === 'other') {
          if (otherBlock) otherBlock.hidden = false;
          if (otherInput) { otherInput.focus(); const v = parseFloat(otherInput.value); state[field] = isFinite(v) && v > 0 ? v : null; }
          else state[field] = null;
        } else {
          if (otherBlock) { otherBlock.hidden = true; if (otherInput) otherInput.value = ''; }
          state[field] = parseFloat(value);
        }
        recompute();
      });
    });
    if (otherInput) {
      otherInput.addEventListener('input', () => {
        const raw = otherInput.value.trim();
        if (!raw) { state[field] = null; if (errEl) { errEl.textContent = ''; errEl.hidden = true; } recompute(); return; }
        const v = parseFloat(raw);
        const limitKey = field === 'vial' ? 'vial_mg' : (field === 'bac' ? 'total_ml' : 'dose_mg');
        const check = validateField(limitKey, v);
        if (check.valid) { state[field] = v; if (errEl) { errEl.textContent = ''; errEl.hidden = true; } }
        else { state[field] = null; if (errEl) { errEl.textContent = check.error; errEl.hidden = false; } }
        recompute();
      });
    }
  });

  // Syringe type toggle (U-100 only)
  $$('[data-syringe]').forEach((btn) => {
    btn.addEventListener('click', () => {
      state.syringe = btn.dataset.syringe;
      $$('[data-syringe]').forEach((b) => {
        const active = b === btn;
        b.classList.toggle('is-calc__pill--active', active);
        b.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      recompute();
    });
  });

  // Next-dose date helper (weekly)
  const lastDoseEl = $('#is-glp1-lastdose');
  const nextDoseOut = $('#is-glp1-nextdose-out');
  if (lastDoseEl && nextDoseOut) {
    lastDoseEl.addEventListener('change', () => {
      const d = new Date(lastDoseEl.value);
      if (isNaN(d.getTime())) { nextDoseOut.hidden = true; return; }
      d.setDate(d.getDate() + 7);
      const opts = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
      nextDoseOut.textContent = 'Next weekly dose due: ' + d.toLocaleDateString('en-AU', opts) + '.';
      nextDoseOut.hidden = false;
    });
  }

  // --- Shareable URL state ---
  function writeUrl() {
    if (!window.history || !window.history.replaceState) return;
    const params = new URLSearchParams();
    if (isFinite(state.vial) && state.vial > 0) params.set('vial', state.vial);
    if (isFinite(state.bac) && state.bac > 0) params.set('vol', state.bac);
    if (isFinite(state.dose) && state.dose > 0) params.set('dose', state.dose);
    if (state.syringe && state.syringe !== 'U-100') params.set('syringe', state.syringe);
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }
  function readUrlPrefill() {
    const p = new URLSearchParams(window.location.search);
    const syr = p.get('syringe');
    if (syr === 'U-100') {
      const btn = root.querySelector('[data-syringe="' + syr + '"]');
      if (btn) btn.click();
    }
    [['vial', 'vial'], ['bac', 'vol'], ['dose', 'dose']].forEach((pair) => {
      const field = pair[0];
      const raw = p.get(pair[1]);
      if (raw === null) return;
      const v = parseFloat(raw);
      if (!isFinite(v) || v <= 0) return;
      let matched = false;
      root.querySelectorAll('.is-calc__pill[data-field="' + field + '"]').forEach((pill) => {
        if (pill.dataset.value === String(v)) { pill.click(); matched = true; }
      });
      if (!matched) {
        const otherPill = root.querySelector('.is-calc__pill--other[data-field="' + field + '"]');
        const otherInput = root.querySelector('.is-calc__other-input[data-field="' + field + '"] input');
        if (otherPill) otherPill.click();
        if (otherInput) { otherInput.value = String(v); otherInput.dispatchEvent(new Event('input', { bubbles: true })); }
      }
    });
  }
  readUrlPrefill();
}
